//changed due to package upgrade
import AppCard from '@projectK/components/AppCard';
import React, { useEffect, useState } from 'react';
import {
  alpha,
  Autocomplete,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import AppGridContainer from '@projectK/components/AppGridContainer';
import PropTypes from 'prop-types';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import dayjs from 'dayjs';
import jwtAxios from '@projectK/services/auth/JWT';

const AuditLogFilter = ({
  setMethodFilter,
  actionFilter,
  setActionFilter,
  setModelFilter,
  modelFilter,
  setStartDateRange,
  startDateRange,
  setDateRange,
  dateRange,
  setPage,
  setUserFilter,
  models,
}) => {
  const companyTheme = CompanyTheme();
  const [isExpandFilter, setIsExpandFilter] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleFilterChange = (filterType, value) => {//NOSONAR
    if (filterType === 'modelFilter') {
      setModelFilter(value || '');
      setPage(0);
    }
    if (filterType === 'userFilter') {
      setUserFilter(value ? value.id : '');
      setPage(0);
    }
    if (filterType === 'createdfromDate') {
      if (value === '') {
        setStartDateRange('');
      } else {
        setStartDateRange(value);
      }
      setPage(0);
    }
    if (filterType === 'createdDate') {
      if (value === '') {
        setDateRange('');
      } else {
        setDateRange(value);
      }
      setPage(0);
    }
    if (filterType === 'methodFilter') {
      setMethodFilter(value || '');
      setPage(0);
    }
    if (filterType === 'actionFilter') {
      setActionFilter(value || '');
      setPage(0);
    }
  };
  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  const toggleFilter = () => {
    setIsExpandFilter(!isExpandFilter);
  };

  const clearFilter = () => {
    setStartDateRange('');
    setDateRange('');
    setModelFilter('all');
    setSelectedUser(null);
    setActionFilter('all');
    setPage(0);
    setUserFilter('');
  };

  let filteredDate = new Date(`${dateRange?.substring(0, 10)}T12:01:00`);
  let filteredFromDate = new Date(
    `${startDateRange?.substring(0, 10)}T12:01:00`,
  );

  useEffect(() => {
    fetchUsersData();
  }, [searchTerm]);
  const fetchUsersData = async () => {
    setLoading(true);
    try {
      const response = await jwtAxios.get(`/users`, {
        params: {
          perPage: 'all',
          search: searchTerm,
        },
      });
      const { data } = response;
      setUsers(data.list);
      setLoading(false);
    } catch (error) {
      setUsers([]);
      setLoading(false);
    }
  };

  return (
    <AppCard
      className='right-main-content'
      title='Filter Audit Logs'
      style={{ position: 'relative' }}
      onClick={toggleFilter}
    >
      <AppGridContainer spacing={5} onClick={stopPropagation}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Autocomplete
            options={(!loading ? users : []).sort((a, b) =>
              (a.first_name + ' ' + a.last_name).localeCompare(
                b.first_name + ' ' + b.last_name,
                undefined,
                { numeric: true, sensitivity: 'base' },
              ),
            )}
            getOptionLabel={(option) =>
              `${option.first_name} ${option.last_name} - ${option.email}`
            }
            value={selectedUser}
            onChange={(_, value) => {
              handleFilterChange('userFilter', value || {});
              setSelectedUser(value);
            }}
            inputValue={searchTerm}
            onInputChange={(_, value) => setSearchTerm(value)}
            renderInput={(params) => (
              <TextField {...params} label='Users' variant='outlined' />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4, sm: 6 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={1}>
              <DatePicker
                label='Audit from'
                sx={{
                  width: '100%',
                  backgroundColor: 'background.paper',
                  color: 'text.primary',
                }}
                value={startDateRange ? filteredFromDate : null}
                renderInput={(params) => <TextField {...params} />}
                onChange={(value) => {
                  const formattedDate = dayjs(value)
                    .startOf('day')
                    .format('YYYY-MM-DD');
                  handleFilterChange(
                    'createdfromDate',
                    formattedDate === 'Invalid Date' ? '' : formattedDate,
                  );
                }}
              />
            </Stack>
          </LocalizationProvider>
        </Grid>
        <Grid size={{ xs: 12, md: 4, sm: 6 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={1}>
              <DatePicker
                label='Audit To'
                minDate={startDateRange ? filteredFromDate : null}
                sx={{
                  width: '100%',
                  backgroundColor: 'background.paper',
                  color: 'text.primary',
                }}
                value={dateRange ? filteredDate : null}
                renderInput={(params) => <TextField {...params} />}
                onChange={(value) => {
                  const formattedDate = dayjs(value)
                    .startOf('day')
                    .format('YYYY-MM-DD');
                  handleFilterChange(
                    'createdDate',
                    formattedDate === 'Invalid Date' ? '' : formattedDate,
                  );
                }}
              />
            </Stack>
          </LocalizationProvider>
        </Grid>

        <Grid size={{ xs: 12, md: 4, sm: 6 }}>
          <FormControl fullWidth>
            <InputLabel>Action Type</InputLabel>
            <Select
              value={actionFilter}
              onChange={(e) =>
                handleFilterChange('actionFilter', e.target.value || {})
              }
              label='Action Type'
            >
              {['all', 'CREATE', 'UPDATE', 'DELETE', 'EXPORT', 'UPLOAD'].map(
                (opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt === 'all' ? 'All Actions' : opt}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, md: 4, sm: 6 }}>
          <Autocomplete
            options={models || []}
            getOptionLabel={(option) => option.label}
            value={models?.find((opt) => opt.label === modelFilter) || null}
            onChange={(_, value) =>
              handleFilterChange('modelFilter', value?.label || 'all')
            }
            renderInput={(params) => (
              <TextField {...params} label='Model/Table' variant='outlined' />
            )}
            isOptionEqualToValue={(option, value) =>
              option.label === value.label
            }
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4, sm: 6 }}>
          <Button
            variant='outlined'
            sx={{
              borderColor: companyTheme ? companyTheme.primary.main : '#2196f3',
              '&:hover': {
                borderColor: companyTheme
                  ? alpha(companyTheme.primary.main, 0.8)
                  : alpha('#2196f3', 0.8),
              },
              color: companyTheme ? companyTheme.primary.main : '#2196f3',
            }}
            onClick={clearFilter}
          >
            Clear Filter
          </Button>
        </Grid>
      </AppGridContainer>
    </AppCard>
  );
};

AuditLogFilter.propTypes = {
  methodFilter: PropTypes.string,
  setMethodFilter: PropTypes.func,
  actionFilter: PropTypes.string,
  setActionFilter: PropTypes.func,
  setModelFilter: PropTypes.func,
  modelFilter: PropTypes.string,
  setDateRange: PropTypes.func,
  dateRange: PropTypes.string,
  setPage: PropTypes.func,
  setStartDateRange: PropTypes.func,
  startDateRange: PropTypes.string,
  setUserFilter: PropTypes.func,
  models: PropTypes.array,
};

export default AuditLogFilter;
